"use client";

import { Navbar1 } from "@/components/ui/Standards/navbar";
import { Footer7 } from "@/components/ui/Standards/footer-7";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import Image from "next/image";
import {
  Droplets,
  Wind,
  Home,
  Warehouse,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";

export default function ProblemLosningarPage() {
  const problems = [
    {
      icon: <Droplets className="w-12 h-12 text-primary" />,
      title: "Fukt i badrum",
      slug: "fukt-badrum",
      problems: [
        "Imma på speglar och fönster",
        "Fuktiga väggar som aldrig torkar",
        "Mögelrisk i fogar och hörn",
        "Dålig lukt som sitter kvar",
        "Avflagnad färg eller tapet",
      ],
      causes: [
        "Otillräcklig frånluft",
        "Ingen tilluft",
        "Stängda dörrar",
        "Dålig självdrag",
      ],
      solution: {
        title: "Mini-FTX eller PAX-fläkt ger stabil luftomsättning",
        description:
          "Med Mini-FTX eller en PAX-fläkt (frånluftsfläkt) får du kontrollerad ventilation som för ut fukten snabbt och effektivt. Ingen imma, ingen fukt, ingen mögelrisk.",
        benefits: [
          "Badrummet torkar snabbare efter dusch",
          "Inga fönster som immar igen",
          "Minskar risk för mögel",
          "Ingen dålig lukt",
          "Fungerar oberoende av väder",
        ],
      },
      image: "/Bilder/Mini-FTX/图3.webp",
    },
    {
      icon: <Wind className="w-12 h-12 text-primary" />,
      title: "Tung luft i sovrum",
      slug: "tung-luft-sovrum",
      problems: [
        "Svårt att andas på natten",
        "Vaknar trött och får huvudvärk",
        "Kondens på fönster på morgonen",
        "Dålig luftkvalitet",
        "Hög CO2-halt",
      ],
      causes: [
        "Stängda dörrar och fönster",
        "Otillräcklig tilluft",
        "Självdrag som inte fungerar",
        "För lite luftomsättning",
      ],
      solution: {
        title: "Mini-FTX ger frisk luft hela natten",
        description:
          "Mini-FTX tillför kontinuerligt filtrerad, frisk luft medan den för ut förbrukad luft. Du sover bättre och vaknar piggare.",
        benefits: [
          "Konstant tillförsel av frisk luft",
          "Filtrerar pollen och damm",
          "Tyst drift (10-20 dB)",
          "Ingen kondens på fönster",
          "Bättre sömnkvalitet",
        ],
      },
      image: "/Bilder/Houses/hus3.webp",
    },
    {
      icon: <Warehouse className="w-12 h-12 text-primary" />,
      title: "Lukt och fukt i källare",
      slug: "lukt-fukt-kallare",
      problems: [
        "Instängd, unken lukt",
        "Fukt som tränger in",
        "Kalla, fuktiga väggar",
        "Mögellukt",
        "Kläder och textilier luktar",
      ],
      causes: [
        "Luften står still",
        "Markfukt tränger in",
        "Ingen aktiv ventilation",
        "Låg temperatur",
      ],
      solution: {
        title: "Mini-FTX skapar kontrollerad luftväg",
        description:
          "En Mini-FTX i källaren skapar en konstant luftcirkulation som för ut fukt och lukt, och tillför frisk, torr luft.",
        benefits: [
          "Minskar fukt effektivt",
          "Tar bort unken lukt",
          "Förhindrar mögel",
          "Skapar trivsammare miljö",
          "Kan göra källaren mer användbar",
        ],
      },
      image: "/Bilder/Houses/hus-coast.webp",
    },
    {
      icon: <Home className="w-12 h-12 text-primary" />,
      title: "Ventilation i uthyrningsdel",
      slug: "ventilation-uthyrningsdel",
      problems: [
        "Dålig luftkvalitet för hyresgästen",
        "Fuktproblem",
        "Ingen egen ventilation",
        "Svårt att kontrollera luften",
        "Krav på godkänd ventilation",
      ],
      causes: [
        "Ingen separat ventilation",
        "Stängda dörrar från resten av huset",
        "Otillräcklig luftväxling",
        "Boverkets krav måste uppfyllas",
      ],
      solution: {
        title: "Mini-FTX - perfekt för uthyrningsdelar",
        description:
          "Mini-FTX ger din uthyrningsdel egen, oberoende ventilation utan att påverka resten av huset. Tyst, energieffektiv och minimalt ingrepp.",
        benefits: [
          "Egen, oberoende ventilation",
          "Uppfyller Boverkets krav",
          "Energieffektiv värmeåtervinning",
          "Tyst drift stör inte hyresgästen",
          "Enkel installation utan kanaler",
        ],
      },
      image: "/Bilder/Houses/Hus.webp",
    },
  ];

  return (
    <>
      <Navbar1 />
      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="container px-4 py-16 mx-auto lg:py-24">
          <div className="max-w-4xl mx-auto text-center">
            <Badge className="mb-4">Problem & Lösningar</Badge>
            <h1 className="mb-6 text-5xl font-bold tracking-tight md:text-6xl">
              Vanliga ventilationsproblem och hur du löser dem
            </h1>
            <p className="text-xl leading-relaxed text-muted-foreground">
              Upptäck hur enrumsventilation med Mini-FTX kan lösa dina
              ventilationsproblem effektivt och energismart
            </p>
          </div>
        </section>

        {/* Problems and Solutions */}
        {problems.map((problem, index) => (
          <section
            key={index}
            className={index % 2 === 0 ? "py-16 bg-muted/30" : "py-16"}
          >
            <div className="container max-w-6xl px-4 mx-auto">
              <div
                className={`grid gap-12 lg:grid-cols-2 items-center ${
                  index % 2 === 1 ? "lg:flex-row-reverse" : ""
                }`}
              >
                {/* Image */}
                <div
                  className={`relative h-[400px] rounded-2xl overflow-hidden shadow-2xl ${
                    index % 2 === 1 ? "lg:order-2" : ""
                  }`}
                >
                  <Image
                    src={problem.image}
                    alt={problem.title}
                    fill
                    className="object-cover"
                  />
                </div>

                {/* Content */}
                <div className={index % 2 === 1 ? "lg:order-1" : ""}>
                  <div className="mb-6">{problem.icon}</div>
                  <h2 className="mb-6 text-4xl font-bold">{problem.title}</h2>

                  {/* Problems */}
                  <div className="mb-8">
                    <h3 className="mb-4 text-xl font-semibold text-red-600">
                      Vanliga symptom:
                    </h3>
                    <ul className="space-y-2">
                      {problem.problems.map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="mt-1 text-red-600">•</span>
                          <span className="text-muted-foreground">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Causes */}
                  <div className="mb-8">
                    <h3 className="mb-4 text-xl font-semibold">
                      Vanliga orsaker:
                    </h3>
                    <ul className="space-y-2">
                      {problem.causes.map((cause, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="mt-1">•</span>
                          <span className="text-muted-foreground">{cause}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Solution */}
                  <div className="p-6 border-l-4 rounded-lg bg-primary/5 border-primary">
                    <h3 className="mb-3 text-2xl font-bold text-primary">
                      Lösning: {problem.solution.title}
                    </h3>
                    <p className="mb-4 text-muted-foreground">
                      {problem.solution.description}
                    </p>
                    <ul className="space-y-2">
                      {problem.solution.benefits.map((benefit, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-sm">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>
        ))}

        {/* Why Mini-FTX Section */}
        <section className="py-16 bg-primary text-primary-foreground">
          <div className="container max-w-4xl px-4 mx-auto text-center">
            <h2 className="mb-6 text-4xl font-bold">
              Varför är Mini-FTX bättre än traditionell ventilation?
            </h2>
            <div className="grid gap-6 mt-12 md:grid-cols-3">
              <div className="p-6 bg-white/10 rounded-xl backdrop-blur">
                <h3 className="mb-3 text-xl font-semibold">Värmeåtervinning</h3>
                <p className="text-sm opacity-90">
                  Upp till 90% av värmen återvinns - ingen energi slösas bort
                </p>
              </div>
              <div className="p-6 bg-white/10 rounded-xl backdrop-blur">
                <h3 className="mb-3 text-xl font-semibold">Filtrerad luft</h3>
                <p className="text-sm opacity-90">
                  Pollen, damm och partiklar filtreras bort automatiskt
                </p>
              </div>
              <div className="p-6 bg-white/10 rounded-xl backdrop-blur">
                <h3 className="mb-3 text-xl font-semibold">Tyst drift</h3>
                <p className="text-sm opacity-90">
                  10-20 dB - tystare än ett visande löv
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16">
          <div className="container max-w-4xl px-4 mx-auto text-center">
            <h2 className="mb-4 text-3xl font-bold">
              Redo att lösa dina ventilationsproblem?
            </h2>
            <p className="mb-8 text-lg text-muted-foreground">
              Kontakta våra samarbetspartners för kostnadsfri rådgivning och
              offert
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button asChild size="lg" className="rounded-full">
                <Link href="/ventilation-installation">
                  Kontakta våra samarbetspartners{" "}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="rounded-full"
              >
                <Link href="https://miniftx.se">Läs mer om Mini-FTX</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <Footer7 />
    </>
  );
}
